import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { DropdownSelect } from "../../components/DropdownSelect";
import {
  PaginationHeader,
  PaginationFooter
} from "../../components/Pagination";
import { useTranslation } from "react-i18next";
import LearningPathListItem from "./LearningPathListItem";
import ErrorPage from "../MiscPages/ErrorPage";
import { useAutoClickedAssignment } from "../../hooks/autoClickedAssignment";
import UserAssignmentContext from "../../context/UserAssignmentContext";
import { visionURL } from "../../config/environment";
import JsonApiService from "../../services/jsonapiService";

const courseCtoaService = async () =>
  new JsonApiService("course_content", {
    baseUrl: `${visionURL}/learning_center/api`,
    credentials: "include"
  });

const LearningPathListView = ({
  assignments,
  numTotalAssignments,
  onSortByOptionChange,
  sortByOption,
  sortOptions,
  onAssignmentsPerPageChange,
  onPageChange,
  page,
  perPage,
  assignmentsPerPageOptions,
  error,
  refetchAssignments
}) => {
  const {
    onStartAssignment,
    onChangeEnrollment,
    onResumeAssignment,
    onAddToCalendarClick,
    calendarModalOpen,
    setCalendarModalOpen,
    isStarted,
    needsRefetch,
    setNeedsRefetch
  } = useContext(UserAssignmentContext);

  const { t } = useTranslation();

  useAutoClickedAssignment();

  useEffect(() => {
    const fetchSections = async () => {
      if (!calendarModalOpen) return;
      if (
        calendarModalOpen.assignment &&
        !calendarModalOpen.assignment.enrolledCtoaId
      ) {
        onAddToCalendarClick(calendarModalOpen.assignment);
      }
      if (
        calendarModalOpen.assignment &&
        calendarModalOpen.assignment.enrolledCtoaId
      ) {
        const service = await courseCtoaService();
        const response = await service
          .include(["course_sections"])
          .find(calendarModalOpen.assignment.assignedTrainingObjectId)
          .then(response => {
            if (response.status == 200) return response.json();
            else return false;
          })
          .then(response => {
            if (response) {
              let activities = response.included;
              activities = activities.filter(
                activity => activity.attributes.sectionType == "mandatory"
              );
              let firstActivity = activities[0];
              firstActivity = {
                id: firstActivity.id,
                ...firstActivity.attributes,
                courseContentables:
                  firstActivity.relationships.courseContentables.data
              };
              if (firstActivity) {
                onAddToCalendarClick(
                  calendarModalOpen.assignment,
                  firstActivity
                );
              } else {
                onAddToCalendarClick(calendarModalOpen.assignment);
              }
            } else onAddToCalendarClick(calendarModalOpen.assignment);
          });
        return response;
      }
    };
    fetchSections();
  }, [calendarModalOpen.assignment]);

  useEffect(() => {
    if (needsRefetch) {
      if (refetchAssignments) {
        refetchAssignments();
        setNeedsRefetch(false);
      }
    }
  }, [needsRefetch]);

  const handleReserveTime = assignment => open => {
    setCalendarModalOpen({
      assignment: assignment,
      open: open
    });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="stretch" mb={4}>
      <Box
        display="flex"
        alignItems="center"
        mb={{ xs: 4, sm: 0 }}
        flexDirection={{ xs: "column-reverse", sm: "row" }}
      >
        <Box display="flex" flex={2}>
          <PaginationHeader
            numPerPageOptions={assignmentsPerPageOptions}
            numItems={numTotalAssignments}
            numItemsPerPage={perPage}
            currentPage={page}
            itemsType={t("learningPathListView.assignments")}
            onPerPageChange={onAssignmentsPerPageChange}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          flex={1}
          mb={{ xs: 4, sm: 0 }}
        >
          <DropdownSelect
            options={sortOptions}
            value={sortByOption}
            onChange={onSortByOptionChange}
            label={t("sortBy")}
          />
        </Box>
      </Box>
      {assignments.length === 0 && error && <ErrorPage />}
      {assignments.map((assignment, index) => {
        return (
          <LearningPathListItem
            key={index}
            assignment={assignment}
            index={index}
            startCourseClick={() => onStartAssignment(assignment)}
            resumeCourseClick={() => onResumeAssignment(assignment)}
            changeEnrollmentClick={() => onChangeEnrollment(assignment)}
            onAddToCalendarClick={onAddToCalendarClick}
            reserveTimeClick={handleReserveTime(assignment)}
            isStarted={isStarted(assignment)}
          />
        );
      })}
      <PaginationFooter
        numPerPageOptions={assignmentsPerPageOptions}
        numItems={numTotalAssignments}
        numItemsPerPage={perPage}
        currentPage={page}
        onPerPageChange={onAssignmentsPerPageChange}
        onPageChange={onPageChange}
      />
    </Box>
  );
};

LearningPathListView.propTypes = {
  error: PropTypes.any,
  sortMethod: PropTypes.string,
  expandedAssignments: PropTypes.any,
  setExpandedAssignments: PropTypes.func,
  refetchAssignments: PropTypes.func,
  assignments: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The assignment title
       */
      title: PropTypes.string,
      /**
       * The assignment status
       */
      assignmentStatus: PropTypes.oneOf([
        "Complete",
        "Failed",
        "Pending",
        "Enrolled",
        "Not Started"
      ]),
      /**
       * The assignment due date
       */
      dueDate: PropTypes.any,
      /**
       * The user's assignment score
       */
      postScore: PropTypes.number,
      /**
       * The score needed to pass the assignment
       */
      passingPostScore: PropTypes.number,
      /**
       * The total number of assignment activities
       */
      totalActivities: PropTypes.number,
      /**
       * The description of the assignment displayed in the expansion panel
       */
      content: PropTypes.string,
      /**
       * The name of the prerequisite for the assignment.
       * If present, the start button is disabled and it is displayed
       * in bold in the hover tooltip over the start button.
       */
      prerequisites: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
      /**
       * The list of the assignment's activities
       */
      activities: PropTypes.array,
      /**
       * Contains the data for the ILT Info section in the Expansion Panel
       */
      iltInfo: PropTypes.shape({
        /**
         * The object with info about the
         */
        instructor: PropTypes.shape({
          /**
           * The id of the instructor
           */
          id: PropTypes.number,
          /**
           * The name of the instructor to be displayed in the ILT Info section
           */
          name: PropTypes.string
        }),
        /**
         * Contains the location information for the ILT Info Section
         */
        location: PropTypes.shape({
          /**
           * The location's name
           */
          name: PropTypes.string,
          /**
           * The location's line one address
           */
          address1: PropTypes.string,
          /**
           * The location's line two address
           */
          address2: PropTypes.string,
          /**
           * The location's city
           */
          city: PropTypes.string,
          /**
           * The location's state
           */
          state: PropTypes.string,
          /**
           * The location's zip code
           */
          zip: PropTypes.string,
          /**
           * The location's latitude
           */
          lat: PropTypes.number,
          /**
           * The location's longitude
           */
          lng: PropTypes.number
        })
      }),
      /**
       * The current page
       */
      page: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
      /**
       * The current amount of assignments displayed per page
       */
      per: PropTypes.oneOf([PropTypes.number, PropTypes.string])
    })
  ).isRequired
};

export default LearningPathListView;
